import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Drawer, IconButton, Menu, MenuItem, MenuList, Link } from '@mui/material'
import { styled } from '@mui/material/styles'
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined'
import CloseIcon from '@mui/icons-material/Close'

/**
 * Icono de menú estilizado.
 * @private
 */
const StyledMenuIcon = styled(MenuOutlinedIcon)(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: 28,
}))

/**
 * Icono de cierre estilizado.
 * @private
 */
const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: 35,
}))

/**
 * Item de menú estilizado con estados de selección.
 * @private
 */
const StyledMenuItem = styled(MenuItem)(({ theme, selected, isChild }) => ({
  height: '100%',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    height: 60,
    fontSize: 20,
  },
  ...(selected &&
    !isChild && {
      boxSizing: 'border-box',
      color: theme.palette.primary.main,
      backgroundColor: '#ffffff !important',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '3px',
        backgroundColor: '#004a6b',
      },
      [theme.breakpoints.down('sm')]: {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 'auto',
          height: 'auto',
          width: '3px',
          backgroundColor: '#004a6b',
        },
      },
    }),
  ...(selected &&
    isChild && {
      boxSizing: 'border-box',
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
      },
    }),
}))

/**
 * Lista de menú estilizada para el header.
 * @private
 */
const StyledMenuList = styled(MenuList)(({ theme }) => ({
  display: 'flex',
  textAlign: 'center',
  height: '80px', // Altura fija del header
  alignItems: 'stretch', // Los items ocupan toda la altura
  margin: 0,
  padding: 0,
}))

/**
 * @typedef {object} MenuItem
 * @property {string} label - El texto que se muestra en el item del menú.
 * @property {string} [path] - La ruta de navegación cuando se hace clic en el item.
 * @property {function(): void} [action] - Una función personalizada a ejecutar en lugar de navegar.
 * @property {string} [url] - Una URL externa que se abre en una nueva pestaña.
 * @property {MenuItem[]} [children] - Sub-items del menú para crear menús desplegables.
 */

/**
 * @typedef {object} MenuComponentProps
 * @property {MenuItem[]} items - Los items del menú a renderizar.
 * @property {boolean} isMobileWidth - Si es `true`, indica que el ancho de pantalla es móvil.
 * @property {boolean} mobileView - Si es `true`, fuerza la vista móvil (drawer) independientemente del ancho.
 */

/**
 * Un componente de menú de navegación responsivo con soporte para sub-menús.
 *
 * Renderiza un menú horizontal en desktop y un drawer lateral en móvil. Soporta:
 * - Items simples con navegación
 * - Items con sub-menús desplegables (solo en desktop)
 * - Items con acciones personalizadas
 * - Items con enlaces externos
 * - Indicador visual del item activo basado en la ruta actual
 *
 * **Nota:** Este componente requiere `react-router-dom` para la navegación.
 *
 * @component
 * @example
 * const menuItems = [
 *   { label: 'Inicio', path: '/home' },
 *   {
 *     label: 'Productos',
 *     children: [
 *       { label: 'Categoría A', path: '/products/a' },
 *       { label: 'Categoría B', path: '/products/b' },
 *     ],
 *   },
 *   { label: 'Contacto', path: '/contact' },
 *   { label: 'Ayuda', url: 'https://help.example.com' },
 * ];
 *
 * const MyHeader = () => {
 *   const isMobile = useWidth() === 'xs';
 *
 *   return (
 *     <MenuComponent
 *       items={menuItems}
 *       isMobileWidth={isMobile}
 *       mobileView={false}
 *     />
 *   );
 * };
 *
 * @param {MenuComponentProps} props - Las props del componente MenuComponent.
 */
export const MenuComponent = ({ items, isMobileWidth, mobileView }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleNavigate = (item) => {
    setDrawerOpen(!drawerOpen)
    if (item.action) {
      item.action()
    } else if (item.path) {
      navigate(item.path, { state: { cleanup: true } })
    }
  }

  const toggleDrawer = (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return
    }
    setDrawerOpen(!drawerOpen)
  }

  const MenuItemCollapsable = ({ item }) => {
    const [anchorEl, setAnchorEl] = React.useState(null)

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
      setAnchorEl(null)
    }

    return (
      <>
        <StyledMenuItem
          selected={item.children.some((item) => item.path === location.pathname)}
          isChild={false}
          onClick={handleClick}
        >
          {item.label}
        </StyledMenuItem>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItems items={item.children} />
        </Menu>
      </>
    )
  }

  const MenuItems = ({ items }) =>
    items.map((item, i) => (
      <StyledMenuItem
        key={i}
        selected={item.path === location.pathname}
        isChild={true}
        onClick={() => handleNavigate(item)}
      >
        {item.url ? (
          <Link underline="none" href={item.url} color="inherit" target="_blank" rel="noopener">
            {item.label}
          </Link>
        ) : (
          item.label
        )}
      </StyledMenuItem>
    ))

  return isMobileWidth || mobileView ? (
    <>
      <IconButton onClick={toggleDrawer} size="large">
        <StyledMenuIcon />
      </IconButton>
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
        <Box p={2}>
          <Box mb={1}>
            <IconButton onClick={toggleDrawer} size="large">
              <StyledCloseIcon />
            </IconButton>
          </Box>
          {items.map((item, i) => (
            <Box key={i}>
              {item.children && item.children.length ? (
                <Box key={i}>
                  <Box key={i} pb={1} fontSize={20}>
                    <StyledMenuItem
                      selected={item.children.some((item) => item.path === location.pathname)}
                      isChild={false}
                    >
                      {item.label}
                    </StyledMenuItem>
                  </Box>
                  <Box ml={4}>
                    <MenuItems items={item.children} />
                  </Box>
                </Box>
              ) : (
                <StyledMenuItem
                  key={i}
                  selected={item.path === location.pathname}
                  isChild={false}
                  onClick={() => handleNavigate(item)}
                >
                  {item.label}
                </StyledMenuItem>
              )}
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  ) : (
    <StyledMenuList>
      {items.map((item, i) => (
        <Box key={i}>
          {item.children && item.children.length ? (
            <MenuItemCollapsable item={item} />
          ) : (
            <StyledMenuItem
              key={i}
              selected={item.path === location.pathname}
              isChild={false}
              onClick={() => handleNavigate(item)}
            >
              {item.label}
            </StyledMenuItem>
          )}
        </Box>
      ))}
    </StyledMenuList>
  )
}
